import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Grid,
  Tooltip,
} from '@mui/material';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { updateOrganizationViaAdmin } from '../../../../repository/OrganizationDAO';
import { useOrganizationUpdaterProvider } from '../../../../context_providers/organization_context/OrganizationProvider';
import { useToast } from '../../../../context_providers/toast_context/ToastContext';
import { ToastType } from '../../../../context_providers/toast_context/ToastContext';
import BaseModal from '../../../../components/modals/BaseModal';
import { useTranslation } from 'react-i18next';
import {
  ErrorStringKeys,
  StringKeys,
  SuccessStringKeys,
} from '../../../../resources/strings/StringKeys';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoadingData from '../../../../components/loading/LoadingData';
import {
  inputSanitization,
  addressSanitization,
} from '../../../../utils/app_support/InputSanitizaiton';
import { EMAIL_REGEX } from '@/utils/validation/emailValidation';

type OrgValidation = {
  companyName: string | null;
  primaryContact: string | null;
  address: string | null;
  email: string | null;
  joinOrgPin: string | null;
  phoneNumber: string | null;
};

function EditOrganizationViaAdminModal({
  show,
  onHide,
}: {
  show: boolean;
  onHide: () => void;
}) {
  const [companyName, setCompanyName] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [primaryContact, setPrimaryContact] = useState('');
  const [joinOrgPin, setJoinOrgPin] = useState<string>('');
  const [orgCode, setOrgCode] = useState<string>();
  const [requirePin, setRequirePin] = useState(false);
  const [isPinVisible, setIsPinVisible] = useState(false);
  const [loadingAPIData, setLoadingAPIData] = useState(false);
  const { organization } = useOrganizationUpdaterProvider();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [validation, setValidation] = useState<OrgValidation>({
    companyName: null,
    primaryContact: null,
    address: null,
    email: null,
    joinOrgPin: null,
    phoneNumber: null,
  });

  const resetFields = useCallback(() => {
    setCompanyName(organization?.company_name ?? '');
    setAddress(organization?.address ?? '');
    setEmail(organization?.email ?? '');
    setPhoneNumber(organization?.phone_number ?? '');
    setPrimaryContact(organization?.primary_contact ?? '');
    setJoinOrgPin(organization?.join_org_pin ?? '');
    setOrgCode(organization?.org_code ?? '');
    setRequirePin(organization?.join_org_pin != null);
    setIsPinVisible(false);
    setValidation({
      companyName: null,
      primaryContact: null,
      address: null,
      email: null,
      joinOrgPin: null,
      phoneNumber: null,
    });
  }, [organization]);

  const validateFields = () => {
    let isValid = true;
    const normalizedEmail = email.trim();
    const newValidation: OrgValidation = {
      address: null,
      companyName: null,
      email: null,
      joinOrgPin: null,
      phoneNumber: null,
      primaryContact: null,
    };
    if (!companyName || companyName.length === 0) {
      newValidation.companyName = 'error';
      isValid = false;
    }

    if (!address || address.length === 0) {
      newValidation.address = 'error';
      isValid = false;
    }

    if (normalizedEmail && !EMAIL_REGEX.test(normalizedEmail)) {
      newValidation.email = 'error';
      isValid = false;
    }

    if (!matchIsValidTel(phoneNumber)) {
      newValidation.phoneNumber = 'error';
      isValid = false;
    }

    if (!primaryContact || primaryContact.length === 0) {
      newValidation.primaryContact = 'error';
      isValid = false;
    }

    if (requirePin && (!joinOrgPin || joinOrgPin.length < 6)) {
      newValidation.joinOrgPin = 'error';
      isValid = false;
    }

    setValidation(newValidation);
    if (!isValid) setLoadingAPIData(false);
    return isValid;
  };

  useEffect(() => {
    if (organization) resetFields();
  }, [organization, resetFields]);

  useEffect(() => {
    if (!requirePin) setJoinOrgPin('');
  }, [requirePin]);

  useEffect(() => {
    if (show) resetFields();
  }, [show, resetFields]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setterFunction: React.Dispatch<React.SetStateAction<string>>,
    sanitizationFunction = inputSanitization,
  ) => {
    setterFunction(sanitizationFunction(e));
  };

  const editOrganization = async () => {
    try {
      setLoadingAPIData(true);
      if (!validateFields()) return;
      const normalizedEmail = email.trim();
      const emailToSave = normalizedEmail === '' ? null : normalizedEmail;
      const orgPinToUse = requirePin ? joinOrgPin : null;
      await updateOrganizationViaAdmin({
        org_code: orgCode ?? '', // Normally I wouldn't do this, but since we are already
        // validating through validateFields(), it's safe
        address,
        company_name: companyName,
        email: emailToSave,
        join_org_pin: orgPinToUse,
        phone_number: phoneNumber,
        primary_contact: primaryContact,
        roles_with_features_custom:
          organization?.roles_with_features_custom ?? null,
        organization_azure_id: organization?.id ?? '',
      });
      console.log(
        'org in context after save',
        organization?.require_join_approval,
      );

      showToast(
        t(SuccessStringKeys.SUCCESSFULLY_UPDATED_ORGANIZATION, {
          organization: `${organization?.company_name}`,
        }),
        ToastType.SUCCESS,
      );
      onHide();
    } catch (error) {
      console.error(error);
      showToast(
        t(ErrorStringKeys.FAILED_TO_UPDATE_ORGANIZATION, {
          organization: `${organization?.company_name}`,
        }),
        ToastType.DANGER,
      );
    } finally {
      setLoadingAPIData(false);
    }
  };

  const handleClose = () => {
    onHide();
    resetFields();
  };

  return (
    <>
      <BaseModal
        footerContent={undefined}
        open={show}
        onClose={handleClose}
        title={t(StringKeys.EDIT_ORGANIZATION, { organization: companyName })}
        onSubmit={editOrganization}
        submitButtonText={t(StringKeys.SAVE)}
        size="md"
        enableEnterKey={true}
      >
        <FormControl fullWidth variant="outlined" margin="normal">
          <TextField
            label={t(StringKeys.COMPANY_NAME)}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            error={validation.companyName === 'error'}
            helperText={
              validation.companyName === 'error' &&
              t(StringKeys.COMPANY_NAME_CANNOT_BE_EMPTY)
            }
          />
        </FormControl>

        <FormControl fullWidth variant="outlined" margin="normal">
          <TextField
            label={t(StringKeys.ADDRESS)}
            value={address}
            onChange={(e) =>
              handleInputChange(e, setAddress, addressSanitization)
            }
            error={validation.address === 'error'}
            helperText={
              validation.address === 'error' &&
              t(StringKeys.ADDRESS_CANNOT_BE_EMPTY)
            }
          />
        </FormControl>

        <FormControl fullWidth variant="outlined" margin="normal">
          <TextField
            label={t(StringKeys.EMAIL_OPTIONAL)}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={validation.email === 'error'}
            helperText={
              validation.email === 'error' &&
              t(StringKeys.PLEASE_PROVIDE_A_VALID_EMAIL_ADDRESS)
            }
          />
        </FormControl>

        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <FormControl fullWidth variant="outlined" margin="normal">
              <MuiTelInput
                label={t(StringKeys.PHONE_NUMBER)}
                value={phoneNumber}
                onChange={setPhoneNumber}
                error={validation.phoneNumber === 'error'}
                helperText={
                  validation.phoneNumber === 'error' &&
                  t(StringKeys.PLEASE_ENTER_A_VALID_PHONE_NUMBER)
                }
                fullWidth
              />
            </FormControl>
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <FormControl fullWidth variant="outlined" margin="normal">
              <TextField
                label={t(StringKeys.PRIMARY_CONTACT)}
                value={primaryContact}
                onChange={(e) => setPrimaryContact(e.target.value)}
                error={validation.primaryContact === 'error'}
                helperText={
                  validation.primaryContact === 'error' &&
                  t(StringKeys.PRIMARY_CONTACT_CANNOT_BE_EMPTY)
                }
              />
            </FormControl>
          </Grid>
        </Grid>
        {/* PIN Section */}
        <Grid container spacing={2} alignItems="center">
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <FormControl fullWidth variant="outlined" margin="normal">
              <InputLabel>
                {t(StringKeys.REQUIRE_PIN)}
                <Tooltip
                  title={t(
                    StringKeys.ENABLING_PIN_WILL_ALLOW_YOU_TO_SET_A_SIX_DIGIT_PIN,
                  )}
                >
                  <IconButton>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputLabel>
              <Select
                label={
                  <>
                    {t(StringKeys.REQUIRE_PIN)}
                    <IconButton>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </>
                }
                value={requirePin ? 'Enabled' : 'Disabled'}
                onChange={(e) => setRequirePin(e.target.value === 'Enabled')}
              >
                <MenuItem value="Enabled">{t(StringKeys.ENABLED)}</MenuItem>
                <MenuItem value="Disabled">{t(StringKeys.DISABLED)}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {requirePin && (
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <FormControl fullWidth variant="outlined" margin="normal">
                <TextField
                  label={t(StringKeys.ORGANIZATION_PIN)}
                  type={isPinVisible ? 'text' : 'password'}
                  value={joinOrgPin || ''}
                  onChange={(e) => handleInputChange(e, setJoinOrgPin)}
                  error={validation.joinOrgPin === 'error'}
                  helperText={
                    validation.joinOrgPin === 'error' &&
                    t(StringKeys.PLEASE_ENTER_A_VALID_SIX_CHARACTER_PIN)
                  }
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setIsPinVisible(!isPinVisible)}
                      >
                        {isPinVisible ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
              </FormControl>
            </Grid>
          )}
        </Grid>
      </BaseModal>
      <LoadingData show={loadingAPIData} />
    </>
  );
}

export default EditOrganizationViaAdminModal;
